import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError } from '../api-client.js';
import { CardPreviewModal } from '../components/CardPreviewModal.js';
import { HandCard, type HandCardData } from '../components/HandCard.js';

type Filter = 'all' | 'player' | 'action' | 'weather' | 'stadium';

export default function LibraryPage() {
  const { gameKey = '' } = useParams();
  const [cards, setCards] = useState<HandCardData[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [preview, setPreview] = useState<HandCardData | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .listCards(gameKey)
      .then((data) => {
        if (alive) setCards(data as HandCardData[]);
      })
      .catch((e) => {
        if (alive) {
          setError(e instanceof ApiError ? e.message : String(e));
        }
      });
    return () => {
      alive = false;
    };
  }, [gameKey]);

  const filtered = useMemo(() => {
    if (!cards) return [];
    if (filter === 'all') return cards;
    return cards.filter((c) => c.kind === filter);
  }, [cards, filter]);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Card library</h2>
        <span className="font-display text-sm uppercase tracking-wide text-ec-bone-dim">{gameKey}</span>
        <Link
          to={`/decks/${gameKey}/new`}
          className="ec-btn ec-btn--gold ml-auto !py-1.5 text-sm"
        >
          + Build a deck
        </Link>
        <Link to="/" className="font-display text-sm uppercase tracking-wide text-ec-brass hover:underline">
          ← back to games
        </Link>
      </header>

      {error && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {error}
        </div>
      )}

      {!cards && !error && <p className="text-sm text-ec-bone-dim">Loading library…</p>}

      {cards && (
        <>
          <div className="flex flex-wrap gap-2">
            {(['all', 'player', 'action', 'weather', 'stadium'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`ec-btn !py-1.5 text-sm ${filter === f ? '!border-ec-brass !text-ec-brass' : ''}`}
              >
                {f}
              </button>
            ))}
            <span className="ml-auto self-center text-xs text-ec-bone-dim">
              {filtered.length} of {cards.length} cards
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            {filtered.map((card, i) => (
              <HandCard
                key={card.id ?? i}
                card={card}
                onClick={() => setPreview(card)}
              />
            ))}
            {filtered.length === 0 && (
              <p className="text-sm text-ec-bone-dim">No cards in this filter.</p>
            )}
          </div>
        </>
      )}

      {preview && <CardPreviewModal card={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
